import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTwitter, FaInstagram, FaArrowRight, FaBehance, FaPlay, FaAward, FaArrowTrendUp, FaBolt, FaCodeBranch, FaFeather } from 'react-icons/fa6';
import Navbar from './Navbar';
import { motion } from 'framer-motion';
import CallIcon from '../components/CallIcon';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, where, limit } from 'firebase/firestore';
import client2 from '../images/client2.png';

const services = [
  {
    title: 'Product & UX',
    description: 'Research-led UX, design systems, and prototypes that make shipping predictable.',
    points: ['User research & journeys', 'Design systems & UI kits', 'Usability testing']
  },
  {
    title: 'Web & SaaS',
    description: 'Full-stack web and SaaS builds with conversion-first frontends and scalable backends.',
    points: ['React/Next builds', 'APIs & integrations', 'Performance + SEO']
  },
  {
    title: 'Growth & Marketing',
    description: 'Positioning, landing pages, and campaigns that turn traffic into pipeline.',
    points: ['Messaging & positioning', 'Landing page sprints', 'Analytics & CRO']
  },
];

const capabilities = [
  'B2B & SaaS product strategy',
  'Web apps, portals, dashboards',
  'E-commerce & subscription flows',
  'Brand + design language refreshes',
  'Go-to-market landing systems',
  'Multi-channel performance + analytics'
];

const process = [
  { title: 'Discover', body: 'Workshops, research, and prioritization to define the product and KPIs.' },
  { title: 'Design', body: 'Rapid wireframes, prototypes, and a modular design system to stay consistent.' },
  { title: 'Build', body: 'Frontend + backend shipped in tight sprints with quality gates and observability.' },
  { title: 'Launch & Grow', body: 'Instrumentation, CRO, and campaign support to keep the funnel healthy.' },
];

const testimonials = [
  {
    quote: 'Perspective HQ rebuilt our product experience and the new funnel lifted paid conversions by 38% within the first quarter.',
    author: 'Martin Adler, CEO — FirstCorp Arts'
  },
  {
    quote: 'Their team moved from research to live MVP in weeks, keeping every stakeholder aligned and every release stable.',
    author: 'Tanya Manuel, Founder — StartupX'
  },
  {
    quote: 'Reliable, creative, and pragmatic. We finally have a design system and marketing site that scale with the roadmap.',
    author: 'David Kim, CTO — TechFlow'
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [featuredProjects, setFeaturedProjects] = useState([]);

  useEffect(() => {
    const fetchFeaturedProjects = async () => {
      try {
        const projectsCollection = collection(db, 'projects');
        const q = query(
          projectsCollection,
          where('isFeatured', '==', true),
          orderBy('order', 'asc'),
          limit(2)
        );
        const snapshot = await getDocs(q);
        const firebaseProjects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Get hardcoded featured projects
        const hardcodedFeatured = [
          {
            id: 'hardcoded-4',
            title: "Park Mobile App",
            category: "UI/UX Design",
            image: client2,
            link: "https://www.behance.net/gallery/203853089/PARK-Mobile-App",
            description: "The Park mobile app provides users with a simple and efficient way to find and pay for parking spaces. We focused on creating an accessible and smooth user interface that reduces friction in the parking experience.",
            isFeatured: true
          }
        ];
        
        // Combine: Firebase featured first, then hardcoded featured to fill up to 2 total
        const remainingSlots = 2 - firebaseProjects.length;
        const allFeatured = [
          ...firebaseProjects,
          ...hardcodedFeatured.slice(0, Math.max(0, remainingSlots))
        ];
        setFeaturedProjects(allFeatured);
      } catch (error) {
        console.error('Error fetching featured projects:', error);
        // Fallback: try without isFeatured filter
        try {
          const projectsCollection = collection(db, 'projects');
          const q = query(projectsCollection, orderBy('order', 'asc'), limit(2));
          const snapshot = await getDocs(q);
          const firebaseProjects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Include hardcoded featured if we have space
          const hardcodedFeatured = [
            {
              id: 'hardcoded-4',
              title: "Park Mobile App",
              category: "UI/UX Design",
              image: client2,
              link: "https://www.behance.net/gallery/203853089/PARK-Mobile-App",
              description: "The Park mobile app provides users with a simple and efficient way to find and pay for parking spaces. We focused on creating an accessible and smooth user interface that reduces friction in the parking experience.",
              isFeatured: true
            }
          ];
          
          // Combine: Firebase projects first, then hardcoded featured to fill up to 2 total
          const remainingSlots = 2 - firebaseProjects.length;
          const allFeatured = [
            ...firebaseProjects,
            ...hardcodedFeatured.slice(0, Math.max(0, remainingSlots))
          ];
          setFeaturedProjects(allFeatured);
        } catch (fallbackError) {
          console.error('Error fetching projects:', fallbackError);
          // If all else fails, show hardcoded featured
          setFeaturedProjects([{
            id: 'hardcoded-4',
            title: "Park Mobile App",
            category: "UI/UX Design",
            image: client2,
            link: "https://www.behance.net/gallery/203853089/PARK-Mobile-App",
            description: "The Park mobile app provides users with a simple and efficient way to find and pay for parking spaces. We focused on creating an accessible and smooth user interface that reduces friction in the parking experience.",
            isFeatured: true
          }]);
        }
      }
    };

    fetchFeaturedProjects();
  }, []);

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-30" />
        <motion.div
          className="absolute -left-24 top-10 w-72 h-72 bg-perspective-green/15 rounded-full blur-3xl"
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -right-24 top-40 w-60 h-60 bg-perspective-sage/25 rounded-full blur-3xl"
          animate={{ y: [0, 18, 0], x: [0, -10, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-12" />

        {/* Hero */}
        <section className="px-4 pt-8 pb-8 lg:pt-12 lg:pb-12">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-perspective-sage text-sm font-semibold border border-perspective-sage/30 backdrop-blur"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <FaBolt className="text-perspective-sage" />
              Perspective HQ • Product & Growth Studio
            </motion.div>

            <div className="grid lg:grid-cols-[1.6fr_1fr] gap-12 mt-10 items-end">
              <div>
                <motion.h1
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-white"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                >
                  Digital products that ship faster, sell better, and stay loved by users.
                </motion.h1>
                <motion.p
                  className="text-lg sm:text-xl text-perspective-cloud/90 mt-6 max-w-2xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.25 }}
                >
                  We design, build, and grow UX-led SaaS, websites, and marketing experiences.
                  Every release focuses on measurable conversion and a clean, modern brand feel.
                </motion.p>

                <motion.div
                  className="flex flex-col sm:flex-row gap-4 mt-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.35 }}
                >
                  <button
                    onClick={() => navigate('/contact')}
                    className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-perspective-sage text-perspective-carbon font-semibold shadow-elevated hover:scale-[1.01] transition-all duration-300"
                  >
                    Book a call
                    <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => navigate('/work')}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/20 text-perspective-cloud font-semibold bg-white/5 backdrop-blur hover:border-perspective-sage hover:text-perspective-sage transition-all duration-300"
                  >
                    View work
                    <FaPlay className="text-sm" />
                  </button>
                </motion.div>

                <motion.div
                  className="flex flex-wrap gap-3 mt-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.45 }}
                >
                  {['UX & product strategy', 'SaaS builds', 'Web & marketing systems'].map(tag => (
                    <span
                      key={tag}
                      className="px-4 py-2 rounded-full bg-white/10 border border-white/10 text-perspective-cloud text-sm font-medium shadow-card backdrop-blur"
                    >
                      {tag}
                    </span>
                  ))}
                </motion.div>
              </div>

              <motion.div
                className="bg-white/5 backdrop-blur rounded-3xl border border-white/10 p-6 shadow-elevated"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-2xl bg-perspective-sage/15 text-perspective-sage">
                    <FaArrowTrendUp />
                  </div>
                  <div>
                    <p className="text-sm text-perspective-cloud/80">Impact snapshot</p>
                    <p className="text-lg font-semibold text-white">Growth with less noise</p>
                  </div>
                </div>
                <ul className="space-y-4 text-perspective-cloud/90">
                  <li className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-perspective-sage" />
                    <span>Conversion-led UX that reduces friction across onboarding, checkout, and renewals.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-perspective-sage" />
                    <span>Design systems and component libraries to keep every page consistent and fast to ship.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-perspective-sage" />
                    <span>Marketing sites wired for SEO, performance, and analytics from day one.</span>
                  </li>
                </ul>
              </motion.div>
            </div>

            <motion.div
              className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              {[
                { label: 'Products shipped', value: '120+' },
                { label: 'Average lift post-launch', value: '30-60%' },
                { label: 'Markets served', value: 'B2B, SaaS, eCom' },
                { label: 'Response time', value: '<24h' },
              ].map(item => (
                <div key={item.label} className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-card backdrop-blur">
                  <p className="text-sm text-perspective-cloud/80">{item.label}</p>
                  <p className="text-2xl font-semibold text-white mt-2">{item.value}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Services */}
        <section className="px-4 py-16 lg:py-20 bg-perspective-ink/60 backdrop-blur">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em]">What we do</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2">End-to-end delivery without the bloat</h2>
                <p className="text-lg text-perspective-cloud/80 mt-3 max-w-2xl">
                  Strategy, design, build, and growth under one roof so your product, website,
                  and campaigns speak the same language.
                </p>
              </div>
              <button
                onClick={() => navigate('/services')}
                className="inline-flex items-center gap-2 text-perspective-sage font-semibold border border-perspective-sage/30 px-4 py-2 rounded-full hover:border-perspective-sage transition-colors bg-white/5 backdrop-blur"
              >
                Explore services
                <FaArrowRight className="text-sm" />
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur shadow-card p-6 flex flex-col"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-2xl bg-perspective-sage/15 text-perspective-sage">
                      <FaFeather />
                    </div>
                    <h3 className="text-xl font-semibold text-white">{service.title}</h3>
                  </div>
                  <p className="text-perspective-cloud/85 mt-4 mb-5">{service.description}</p>
                  <ul className="space-y-2 text-perspective-cloud/90">
                    {service.points.map(point => (
                      <li key={point} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-perspective-sage" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 rounded-3xl border border-perspective-sage/15 bg-perspective-carbon text-white p-8 grid md:grid-cols-3 gap-6 shadow-elevated">
              <div>
                <p className="text-sm uppercase tracking-[0.12em] text-perspective-sage">Capabilities</p>
                <h3 className="text-2xl font-semibold mt-3">Designed to move from idea to revenue fast</h3>
                <p className="text-perspective-mint/90 mt-3">
                  We plug into your team or run the full stack as your product and growth partner.
                </p>
              </div>
              <div className="md:col-span-2 grid sm:grid-cols-2 gap-3 text-perspective-sand">
                {capabilities.map(item => (
                  <div key={item} className="flex gap-3 rounded-2xl bg-white/5 border border-perspective-sage/20 p-4">
                    <FaCodeBranch className="text-perspective-sage mt-1" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Work */}
        <section className="px-4 py-16 lg:py-20 bg-perspective-carbon">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em]">Work</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white">Selected launches</h2>
                <p className="text-lg text-perspective-cloud/85 mt-3 max-w-2xl">
                  A snapshot of recent platforms, SaaS products, and marketing sites shipping with Perspective HQ.
                </p>
              </div>
              <button
                onClick={() => navigate('/work')}
                className="inline-flex items-center gap-2 text-perspective-sage font-semibold px-4 py-2 rounded-full border border-perspective-sage/30 bg-white/5 backdrop-blur shadow-card hover:-translate-y-0.5 transition-transform"
              >
                View full portfolio
                <FaArrowRight className="text-sm" />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 mt-10">
              {featuredProjects.length > 0 ? (
                featuredProjects.map((project, index) => (
                  <motion.div
                    key={project.id || index}
                    className="group rounded-3xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur shadow-elevated flex flex-col"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-perspective-carbon/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <div className="p-6 flex-1 flex flex-col gap-3">
                      <span className="inline-flex w-fit items-center gap-2 text-sm text-perspective-sage font-semibold">
                        <span className="h-2 w-2 rounded-full bg-perspective-sage" />
                        {project.category}
                      </span>
                      <h3 className="text-2xl font-semibold text-white">{project.title}</h3>
                      <p className="text-perspective-cloud/85 flex-1">{project.description}</p>
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-perspective-sage font-semibold hover:gap-3 transition-all duration-200"
                      >
                        View project
                        <FaArrowRight className="text-sm" />
                      </a>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-2 text-center py-12">
                  <p className="text-perspective-cloud/60">No featured projects yet. Add projects in the admin panel.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <CallIcon />

        {/* Process */}
        <section className="px-4 py-16 lg:py-20 bg-perspective-ink/70 backdrop-blur">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em]">How we work</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">A delivery rhythm built for momentum</h2>
              <p className="text-lg text-perspective-cloud/85 max-w-3xl">
                Transparent sprints, design systems, and instrumentation so teams stay confident from first wireframe to post-launch growth.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6 mt-10">
              {process.map((step, index) => (
                <motion.div
                  key={step.title}
                  className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5 shadow-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 flex items-center justify-center rounded-2xl bg-perspective-sage/15 text-perspective-sage font-bold">
                      {index + 1}
                    </span>
                    <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                  </div>
                  <p className="text-perspective-cloud/85 mt-3">{step.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 py-16 lg:py-20 bg-perspective-carbon text-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em]">Client words</p>
              <h2 className="text-3xl sm:text-4xl font-bold">Partners who move fast with us</h2>
              <p className="text-lg text-perspective-mint/90 max-w-3xl">
                Collaborative, transparent, and outcome-focused. That’s how we keep momentum and protect quality.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {testimonials.map((item, index) => (
                <motion.div
                  key={item.author}
                  className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur p-6 flex flex-col gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="flex gap-2 text-perspective-sage">
                    {[...Array(5)].map((_, i) => (
                      <FaAward key={i} size={14} />
                    ))}
                  </div>
                  <p className="text-lg leading-relaxed text-perspective-sand/90">“{item.quote}”</p>
                  <p className="text-sm text-perspective-mint font-semibold">{item.author}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-r from-perspective-carbon to-perspective-border text-white shadow-elevated border border-perspective-sage/15">
            <div className="p-10 md:p-12 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-2xl">
                <p className="text-sm uppercase tracking-[0.12em] text-perspective-mint">Let’s build</p>
                <h3 className="text-3xl sm:text-4xl font-bold mt-2">Ready for a modern, conversion-ready product?</h3>
                <p className="text-lg text-perspective-mint/90 mt-3">
                  Tell us about your goals—launching, migrating, or scaling. We’ll share a lean plan within 48 hours.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={() => navigate('/contact')}
                  className="group inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-perspective-sage text-perspective-carbon font-semibold hover:translate-y-[-2px] transition-all"
                >
                  Start a project
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/work')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors"
                >
                  See our work
                </button>
              </div>
            </div>
            <div className="bg-black/10 border-t border-white/10 px-10 md:px-12 py-6 grid sm:grid-cols-3 gap-4 text-sm text-perspective-mint/90">
              <div>
                <p className="font-semibold text-white">Email</p>
                <p>info@perspectivehq.co.za</p>
              </div>
              <div>
                <p className="font-semibold text-white">Call</p>
                <p>+27 69 339 5619</p>
              </div>
              <div>
                <p className="font-semibold text-white">Location</p>
                <p>Durban, South Africa</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-perspective-carbon text-white">
          <div className="max-w-6xl mx-auto px-4 py-14">
            <div className="grid md:grid-cols-4 gap-8 mb-10">
              <div className="md:col-span-2">
                <img 
                  src="/perspectivelogo.png" 
                  alt="Perspective HQ" 
                  className="h-8 w-auto mb-4"
                />
                <p className="text-perspective-mint/90 max-w-xl">
                  We are a UX, web, and SaaS studio focused on clarity, speed, and measurable growth. From idea to launch, we keep teams aligned and momentum high.
                </p>
                <div className="flex gap-3 mt-6">
                  <a href="https://twitter.com/perspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaTwitter size={16} />
                  </a>
                  <a href="https://instagram.com/theperspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaInstagram size={16} />
                  </a>
                  <a href="https://www.behance.net/skaina" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaBehance size={16} />
                  </a>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Navigate</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <button onClick={() => navigate('/work')} className="block hover:text-perspective-sage transition-colors">Work</button>
                  <button onClick={() => navigate('/services')} className="block hover:text-perspective-sage transition-colors">Services</button>
                  <button onClick={() => navigate('/about')} className="block hover:text-perspective-sage transition-colors">About</button>
                  <button onClick={() => navigate('/blog')} className="block hover:text-perspective-sage transition-colors">Blog</button>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Contact</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <p>info@perspectivehq.co.za</p>
                  <p>+27 69 339 5619</p>
                  <p>Durban, South Africa</p>
                </div>
              </div>
            </div>
            <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-perspective-mint/80">
              <p>© 2021-2025 Perspective HQ. All rights reserved.</p>
              <div className="flex gap-4">
                <button onClick={() => navigate('/privacy-policy')} className="hover:text-perspective-sage transition-colors">Privacy Policy</button>
                <button onClick={() => navigate('/terms-of-service')} className="hover:text-perspective-sage transition-colors">Terms of Service</button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Home;